import { callOrganizationServiceApi } from '../../../apis/organizationService';

/**
 * Deletes an office review through organization-service.
 *
 * The counterpart to `getOfficeReviews`: once the table reads from
 * organization-service, Remove writing back to the monolith would leave the row
 * deleted in one store and present in the other until the next ingest.
 *
 * **Addressed by organization-service's own `reviewId`** — the route is
 * `DELETE /admin/office-reviews/{id}` and `{id}` is `location_review.id`
 * (GOTU-7208). Not the monolith's review id, and this is not an arbitrary
 * choice on their side: the delete publishes the whole entity with
 * `deletedAtUtc` set rather than a tombstone, precisely because the monolith
 * has never seen organization-service's UUID and cannot resolve it — the
 * consumer works from the `professionalId`/`jobId` natural key instead. A route
 * keyed by monolith id has no place in that flow.
 *
 * The monolith's own `DELETE /admin/reviews/{reviewId}` stays, and still serves
 * office→professional reviews on professional admin pages. The two deletion
 * paths coexist deliberately, one per page type, mirroring the two read paths.
 *
 * Every row has a `reviewId`, so unlike the monolith route there is no review
 * this cannot address — which is why no removability gate depends on an id
 * being present.
 *
 * Responds 204 on success, 204 again for an already-deleted review (idempotent,
 * publishing nothing), and 404 when no such review exists.
 *
 * @param {string} reviewId - organization-service's `location_review.id`
 * @returns {Promise<void>}
 */
export const deleteOfficeReview = async (reviewId) => {
  // A falsy id would send `DELETE .../office-reviews/undefined`, which answers
  // 404 — indistinguishable from a review someone else already removed. Fail
  // loudly on the wiring bug instead.
  if (!reviewId) {
    throw new Error('deleteOfficeReview requires a reviewId');
  }

  await callOrganizationServiceApi('delete', `admin/office-reviews/${reviewId}`);
};
